import {
  Component,
  effect,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  signal,
} from '@angular/core';
import { SurveyRenderControllerStore } from '../render-store/render.store';
import { UnifiedSurveyStore } from '../render-store/unified-survey.store';
import { SafeScriptComponent } from '../script-injector/safe-script/safe-script.component';
import {
  ComposedSurveyPlugin,
  PluginHookName,
} from '@hyper-inferno/survey-elements-core';
import { ElementErrorText } from '../element-error-text/element-error-text';

@Component({
  selector: 'lib-plugin-host',
  imports: [SafeScriptComponent, ElementErrorText],
  templateUrl: './plugin-host.html',
  styleUrl: './plugin-host.css',
})
export class PluginHost implements OnDestroy {
  @Input({ required: true }) pluginDef!: ComposedSurveyPlugin;

  renderStore = inject(SurveyRenderControllerStore);
  surveyBotStore = inject(UnifiedSurveyStore);
  private el = inject(ElementRef);

  public safeHtml: string = '';
  public validationError = signal<string | null>(null);
  public pluginValueData = signal<any>(undefined);

  // Scoped storage for the plugin logic to keep custom variables between hooks
  private pluginInternalState: any = {};

  constructor() {
    // Re-run logic if survey data changes
    effect(() => {
      const registry = this.renderStore.registry();
      this.triggerHook('onDataChange', registry);
    });

    effect(() => {
      // 1. Watch for global submit attempts
      const attempt = this.renderStore.submitAttempted();

      // 2. If a submit was attempted, force run validation
      const id = this.pluginDef.usageId;
      const error = this.validationError();
      const data = this.pluginValueData();
      const isValid = error === null;

      this.renderStore.updatePluginState(
        String(id) as string,
        data,
        isValid,
        error,
        () => {
          const valid = !this.validationError();
          return valid;
        },
        () => {
          this.runSubmitHook();
        },
      );
    });
  }

  ngOnInit() {
    this.safeHtml = this.pluginDef.html || '';
    // 2. Hydrate the plugin data signal with initial backend/saved data if it exists
    if (this.pluginDef.answerObject) {
      // Direct deep copy/clone recommended to prevent mutating the original input reference
      this.pluginValueData.set(JSON.parse(JSON.stringify(this.pluginDef.answerObject)));
    }

    // 3. Immediately register this initial state with your store so the backend payload 
    // is ready even if the user submits without interacting with the plugin.
    const id = this.pluginDef.usageId;
    this.renderStore.updatePluginState(
      String(id),
      this.pluginValueData(),
      this.validationError() === null,
      this.validationError(),
      () => !this.validationError(),
      () => this.runSubmitHook()
    );

    setTimeout(() => this.triggerHook('onPageInit'), 0);
  }

  public triggerHook(hookName: PluginHookName, payload: any = null) {
    const script = this.pluginDef.hooks?.[hookName];
    if (!script) return;

    if (hookName === 'onBeforeSubmit') {
      this.validationError.set(null);
    }

    const sessionId = this.surveyBotStore.sessionId();
    const surveyId = this.surveyBotStore.context();
    const context = {
      config: this.pluginDef.instanceValues,
      registry: this.renderStore.registry(),
      container: this.el.nativeElement,
      pageId: this.renderStore.currentPageState()?.page?.id,
      survey_context: {
        survey_id: surveyId,
        survey_session_id: sessionId,
        ...this.pluginDef.context
      },
      state: {
        ...this.pluginInternalState,
      },

      // State persistence across hooks for this plugin instance

      setInvalid: (msg: string) => {
        this.validationError.set(msg);
      },
      getAnswer: (id: string) => this.renderStore.registry()[id]?.data,
      patchData: (patch: Partial<any>) => {
        this.pluginValueData.update(currentState => ({
          ...currentState,
          ...patch
        }));
      },
      pluginData: (() => this.pluginValueData())
    };

    try {
      const fn = new Function('context', 'payload', script);
      fn(context, payload);

      // Save state back
      this.pluginInternalState = context.state;

      if (hookName === 'onBeforeSubmit') {
        this.surveyBotStore.markPluginAsCompleted(this.pluginDef.usageId);
      }
    } catch (e) {
      console.error(`Plugin Execution Error [${this.pluginDef.usageId}]:`, e);
    }
  }

  ngOnDestroy() {
    this.triggerHook('onCleanup');
    this.renderStore.clearPlugin(this.pluginDef.usageId);
  }

  public runSubmitHook() {
    this.triggerHook('onBeforeSubmit');
  }
}
